import React, { useState } from "react";
import {
  Box,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ReactComponent as EthiquinNoData } from "../../../../../assets/images/Ethiquin/ethiquin_no_data.svg";
import { useReportContext } from "../../../ReportContext/useReportContext";
import { AvatarLetter } from "../../../../MainComponents/Avatar/AvatarLetter";
import { parseDateToISOString4 } from "../../../../../helpers/dateTransform";
import EGAccordion from "../../../../MainComponents/Accordion/EGAccordion/EGAccordion";
import "./Historical.scss";

export const Historical = ({ history }) => {
  // ---> Custom Hooks <--- //
  const [t] = useTranslation("report");
  const theme = useTheme();
  const { status_locales } = useReportContext();
  // ---> Initial States <--- //
  // Expanded default historical element
  const [expandedHistoryID, setExpandedHistoryID] = useState(
    history.at(-1)?.assigned
  );
  // Show more change historical items
  const [showHistory, setShowHistory] = useState(false);
  // Number of history items shown by default
  const [showDefaultHistory, setShowDefaultHistory] = useState(4);

  // View less/more history elements
  const toggleShowHistory = () => {
    setShowHistory((prev) => !prev);
  };

  // View more
  const toggleMoreInfo = (assigned) => {
    setExpandedHistoryID((prev) => (prev === assigned ? null : assigned));
  };

  // Calculation of history items displayed on screen
  const historyLength = history?.length - showDefaultHistory;

  // Label vew more/less
  let viewMore;
  if (historyLength === 1) {
    viewMore = `${t("Care.view")} 1 ${t("Care.more_change")}`;
  } else {
    viewMore = `${t("Care.view_others")} ${historyLength} ${t(
      "Care.more_changes"
    )}`;
  }

  // History panel items
  const historicalPanel = [
    {
      id: Array.isArray(history) && history?.length === 0 ? null : 1,
      title:
        Array.isArray(history) && history?.length === 0
          ? t("Care.no_status_change_history")
          : t("Care.status_change_history"),
      contentBorderColor: theme.palette.blueGray50.main,
      content:
        Array.isArray(history) && history?.length === 0 ? (
          <div className="no-data-etiquin">
            <EthiquinNoData width="125px" height="125px" />
          </div>
        ) : (
          <>
            <div id="recent-reports">
              {/* Show less */}
              {showHistory && history?.length > showDefaultHistory && (
                <div onClick={toggleShowHistory} className="--showHistory">
                  <span className="--showHistory__line"></span>
                  <span className="--showHistory__lineText">{`${t(
                    "Care.view_less"
                  )}`}</span>
                  <span className="--showHistory__line"></span>
                </div>
              )}

              <Stack className="history" spacing={2.5}>
                {Array.isArray(history) &&
                  [...history]
                    .sort((a, b) => new Date(a.assigned) - new Date(b.assigned))
                    .reverse()
                    ?.slice(
                      0,
                      showHistory ? history?.length : showDefaultHistory
                    )
                    .map((item, i) => (
                      <Paper
                        key={i}
                        sx={{
                          mx: 1,
                          borderLeft: `${
                            status_locales[item.status].color
                          } 10px solid`,
                        }}
                        className="dyBorder1 rounded"
                      >
                        <Stack
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          flexDirection="row"
                          flexWrap="wrap"
                          mx={2}
                          py={0.75}
                          className={
                            expandedHistoryID === item.assigned
                              ? "border-bottom"
                              : ""
                          }
                        >
                          <Typography variant="h6">
                            {status_locales[item.status].label}
                          </Typography>
                          {expandedHistoryID !== item.assigned && (
                            <Link
                              onClick={() => toggleMoreInfo(item.assigned)}
                              underline="hover"
                              textAlign="end"
                            >
                              {t("Care.more_details")}
                            </Link>
                          )}
                        </Stack>

                        {expandedHistoryID === item.assigned && (
                          <Box mx={2} mb={2} mt={0.5}>
                            <Typography
                              color="secondary"
                              variant="body2"
                              component="span"
                            >
                              {item?.user?.name && (
                                <ListItem dense sx={{ px: 0 }}>
                                  <ListItemAvatar
                                    sx={{ padding: 0, minWidth: "35px" }}
                                  >
                                    <AvatarLetter
                                      contact={{ name: item.user.name }}
                                    />
                                  </ListItemAvatar>
                                  <ListItemText
                                    primaryTypographyProps={{ fontWeight: 600 }}
                                    primary={item.user.name}
                                    secondaryTypographyProps={{
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      textOverflow: "ellipsis",
                                    }}
                                  />
                                </ListItem>
                              )}

                              {`${t(
                                "Care.updated_at"
                              )}: ${parseDateToISOString4(item.assigned)}`}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    ))}

                {/* Show more */}
                {!showHistory && history.length > showDefaultHistory && (
                  <div onClick={toggleShowHistory} className="--showHistory">
                    <span className="--showHistory__line"></span>
                    <span className="--showHistory__lineText">{viewMore}</span>
                    <span className="--showHistory__line"></span>
                  </div>
                )}
              </Stack>
            </div>
          </>
        ),
    },
  ];
  return <EGAccordion panels={historicalPanel} defaultExpanded={1} />;
};
